import type { ReactNode } from 'react'
import type { ChartPane } from '@/components/charts/time-chart'
import type { LiveSpec } from '@/lib/market/types'

import { useCallback, useMemo } from 'react'
import { dayTicks } from '@/components/charts/scales'
import { useQuote } from '@/components/market/live-provider'
import { formatDay } from '@/lib/market/calendar'
import { formatCompact, formatNumber, formatPercent, formatSignedKes } from '@/lib/market/format'
import { orderFlowSplit } from '@/lib/market/orderflow'
import Legend from '@/components/charts/legend'
import TimeChart from '@/components/charts/time-chart'

interface StockFlowsProps {
    ticker: string
    live: LiveSpec
    foreign: { d: number[], buy: number[], sell: number[] }
    orderFlow: { sigma: number, noise: number }
}

const StockFlows = ({ ticker, live, foreign, orderFlow }: StockFlowsProps): ReactNode => {
    const quote = useQuote(live)
    const split = orderFlowSplit(quote, orderFlow.sigma, orderFlow.noise)
    const net = useMemo(() => foreign.buy.map((value, index) => value - foreign.sell[index]), [foreign])
    const labels = useMemo(() => foreign.d.map(day => formatDay(day, 'weekday')), [foreign.d])
    const xTicks = useCallback((width: number) => dayTicks(foreign.d, width), [foreign.d])
    const total = net.reduce((acc, value) => acc + value, 0)
    const gross = foreign.buy.reduce((acc, value) => acc + value, 0) + foreign.sell.reduce((acc, value) => acc + value, 0)

    const panes: ChartPane[] = useMemo(() => [{
        id: 'foreign',
        height: 150,
        zero: true,
        format: value => formatSignedKes(value, 0),
        series: [{
            kind: 'bars',
            id: 'net',
            label: 'Net foreign',
            values: net,
            color: (_index: number, value: number) => (value >= 0 ? 'var(--up)' : 'var(--down)'),
            format: value => formatSignedKes(value)
        }]
    }], [net])

    return (
        <div className='space-y-5'>
            <div>
                <div className='mb-2 flex items-center justify-between text-xs'>
                    <span className='font-medium text-foreground'>Order flow this session</span>
                    <span className='text-muted-foreground'>{formatCompact(quote.volume)} shares</span>
                </div>

                <div className='flex h-3 gap-0.5 overflow-hidden rounded-full' role='img' aria-label={`${formatPercent(split.share * 100, 0, false)} of volume was buyer initiated`}>
                    <span className='rounded-l-full bg-up' style={{ width: `${split.share * 100}%` }} />
                    <span className='rounded-r-full bg-down' style={{ width: `${(1 - split.share) * 100}%` }} />
                </div>

                <dl className='mt-2 grid grid-cols-3 gap-2 text-xs'>
                    <div>
                        <dt className='text-muted-foreground'>Buyer initiated</dt>
                        <dd className='font-semibold tabular-nums text-up'>{formatNumber(split.buy)}</dd>
                    </div>
                    <div>
                        <dt className='text-muted-foreground'>Seller initiated</dt>
                        <dd className='font-semibold tabular-nums text-down'>{formatNumber(split.sell)}</dd>
                    </div>
                    <div>
                        <dt className='text-muted-foreground'>Imbalance</dt>
                        <dd className='font-semibold tabular-nums text-foreground'>{formatPercent(split.imbalance * 100, 1)}</dd>
                    </div>
                </dl>
            </div>

            <div>
                <div className='mb-2 flex flex-wrap items-baseline justify-between gap-2 text-xs'>
                    <span className='font-medium text-foreground'>Foreign investors, last 30 sessions</span>
                    <span className='text-muted-foreground'>
                        Net <span className={total >= 0 ? 'text-up' : 'text-down'}>{formatSignedKes(total)}</span>, gross {formatCompact(gross)}
                    </span>
                </div>

                <Legend
                    className='mb-1'
                    items={[
                        { key: 'in', label: 'Net buying', color: 'var(--up)', shape: 'rect' },
                        { key: 'out', label: 'Net selling', color: 'var(--down)', shape: 'rect' }
                    ]}
                />

                <TimeChart count={foreign.d.length} labels={labels} xTicks={xTicks} panes={panes} ariaLabel={`Net foreign investor flow in ${ticker} over 30 sessions`} />
            </div>
        </div>
    )
}

export default StockFlows
